import { useState, useCallback, useEffect, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { useShareList } from '@/api';

interface UsePersonalListShareOptions {
  urlKey: string;
  isOpen: boolean;
  /** Current note-sharing state for the list, used to seed the toggle without mutating it on open. */
  isNotesPublic?: boolean;
}

const TOGGLE_COOLDOWN_MS = 30000; // 30 second cooldown between toggles

/**
 * Hook for managing personal list sharing state
 *
 * Flow:
 * 1. Modal opens - seeds the toggle from the list's current note-sharing state, then
 *    generates/refreshes the share URL by re-sending that SAME (unchanged) value —
 *    opening the modal must never flip note-sharing on or off as a side effect.
 * 2. User toggles With/Without Notes - immediately calls API to update setting (a deliberate change)
 * 3. URL updates after each toggle
 * 4. Rate limited: 30 second cooldown between toggles
 */
export function usePersonalListShare({ urlKey, isOpen, isNotesPublic }: UsePersonalListShareOptions) {
  const toast = useToast();
  const [shareUrl, setShareUrl] = useState('');
  const [shareWithNotes, setShareWithNotes] = useState(() => isNotesPublic ?? false);
  const [isCooldown, setIsCooldown] = useState(false);
  const lastToggleRef = useRef<number>(0);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { mutateAsync: shareList, isPending: isSharing } = useShareList();

  // Generate/update share URL with given notes setting
  const updateShareSettings = useCallback(async (withNotes: boolean) => {
    try {
      const result = await shareList({
        urlKey,
        shareNotes: withNotes,
      });
      setShareUrl(result.url);
    } catch (error) {
      console.error('Failed to update share settings:', error);
      toast({
        title: 'Failed to update share settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [urlKey, shareList, toast]);

  // Handle notes toggle - immediately updates server with rate limiting
  const handleNotesToggle = useCallback(async (withNotes: boolean) => {
    const now = Date.now();
    const timeSinceLastToggle = now - lastToggleRef.current;

    // Check cooldown
    if (timeSinceLastToggle < TOGGLE_COOLDOWN_MS) {
      return;
    }

    // Update last toggle time and start cooldown
    lastToggleRef.current = now;
    setIsCooldown(true);

    // Clear any existing cooldown timer
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
    }

    // Set cooldown timer
    cooldownTimerRef.current = setTimeout(() => {
      setIsCooldown(false);
    }, TOGGLE_COOLDOWN_MS);

    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch {
      // Haptics not available
    }

    setShareWithNotes(withNotes);
    await updateShareSettings(withNotes);
  }, [updateShareSettings]);

  // Initialize on open: re-seed the toggle from the list's CURRENT note-sharing
  // state (not a hardcoded value) so opening the modal never mutates it — only
  // fetch/refresh the share URL using that unchanged value.
  useEffect(() => {
    if (isOpen) {
      const currentShareWithNotes = isNotesPublic ?? false;
      setShareWithNotes(currentShareWithNotes);
      updateShareSettings(currentShareWithNotes);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShareUrl('');
      setIsCooldown(false);
      lastToggleRef.current = 0;
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
    };
  }, []);

  return {
    shareUrl,
    shareWithNotes,
    isSharing: isSharing || isCooldown,
    handleNotesToggle,
  };
}
